import { AccountData, AccountAmortizationData, AccountStateDto } from '../account/account.model';
import { UserPermissionData, readWrite } from '../user/user-permission/user-permission.data';
import {
  QueryParams,
  QueryResult,
  LoanInfoDto,
  InitiatorType,
  TransactionType,
  TransactionStatus,
  SubtransactionData,
  parseNumber,
  parseNumberArray,
  LiquidityEvent,
  Address,
  AccountTransferData,
  PaymentFrequency,
  ProductCode,
  LoanStatus,
  FailureReasonBaseData
} from 'common';
import { Note } from '../shared/note/note.model';
import { UserData } from '../user/user.model';
import { AdvisorData } from '../advisor/advisor.model';
import { PromotionSelectOptionData } from '../admin/promotion/models/promotion.model';
import { GeographicalPosition } from "common";
import { TransactionNextStatus, TransferMethod, TransferSource } from 'projects/common/src/public_api';

export class TransactionData {
    id?: number;
    entityId?: number;
    accountId?: number;
    loanId?: number;
    loanNumber?: string;
    originalLoanId?: number;
    originalClientId?: string;
    loanloanFacilityId?: number;
    loanFacilityName?: string;
    businessName?: string;
    entityNumber?: string;
    uniqueId?: string;
    date?: Date;
    processDate?: Date;
    underwritingDate?: Date;
    financeDate?: Date;
    completeDate?: Date;
    cancellationDate?: Date;
    declinedDate?: Date;
    amount?: number;
    displayAmount?: number;
    initiator?: string;
    initiatorType?: InitiatorType;
    initiatorId?: number;
    modifier?: string;
    creator?: string;
    creatorType?: InitiatorType;
    advisor?: string;
    type?: TransactionType;
    status?: TransactionStatus;
    nextStatus?: TransactionNextStatus;
    maintained?: string;
    disbursedAmount?: number;
    thirdPartyPayoffAmount?: number;
    deferredFeeAmount?: number;
    cashFeeAmount?: number;
    principalAmount?: number;
    interestAmount?: number;
    notes?: Note[];
    failureReasons?: string[];
    subtransactions?: SubtransactionData[];
    bankAccountId?: number;
    isScreenShareRequired?: boolean;
    isAbleToUnderwrite?: boolean;
    isTransferBankAccountLinked?: boolean;
    missingInformation?: string[];
    wasScreenShareRequiredAtUWTime?: boolean;
    wasAbleToUnderwriteAtUWTime?: boolean;
    wasTransferBankAccountLinkedAtUWTime?: boolean;
    transferAmount?: number;
    isFirstDraw?: boolean;
    isFirstDrawOfLoan?: boolean;
    loanModificationSnapshotId?: number;
    closingBalance?: number;
    outstandingInterest?: number;
    outstandingPrincipal?: number;
    creditLimit?: number;
    utilization?: number;
    totalPrincipal?: number;
    totalInterest?: number;
    totalDeferredFees?: number;
    totalCashFees?: number;
    totalFees?: number;
    totalPayback?: number;
    promotions?: PromotionSelectOptionData[];
    competitors?: number[];
    loanPrincipalBalance?: number;
    loanCreditLimit?: number;
    drawRequirements?: DrawRequirementData[];
    drawRequirementsHash?: string;
    customerDrawGeographicalPosition?: GeographicalPosition;
    businessAddress?: Address;
    transferMethod?: TransferMethod;
    transferSource?: TransferSource;
    bankAccount?: string;
    payoffDestinations?: PayoffDestinationData[];
    payoffTotal?: number;
    payoffCapturedTotal?: number;
    payoffTotalDiffers?: boolean;
    subtransactionsFeeTypes?: string;
    relatedTransactionId?: number;
}

export interface PayoffDestinationData {
    lender?: string;
    bankName?: string;
    account?: string;
    amount?: number;
}

export function toPayoffDestinationLine(destination: PayoffDestinationData): string {
    return `${destination.lender} ${destination.account}`;
}

export function toPayoffDestinationsText(destinations: PayoffDestinationData[] | undefined): string {
    return (destinations ?? []).map(toPayoffDestinationLine).join(', ');
}

export const CanUserEditTransactionInStatus: UserPermissionData[] = [];

export const CanUserMoveTransactionToStatus: UserPermissionData[] = [];

function UpdateCanUserEditTransactionInStatusArray(status: TransactionStatus, permission: UserPermissionData): void {
    CanUserEditTransactionInStatus[status] = permission;
}

function UpdateCanUserMoveTransactionToStatusArray(status: TransactionStatus, permission: UserPermissionData): void {
    CanUserMoveTransactionToStatus[status] = permission;
}

UpdateCanUserEditTransactionInStatusArray(TransactionStatus.Pending, readWrite('servicing-editing-pending-transactions'));
UpdateCanUserEditTransactionInStatusArray(TransactionStatus.Processed, readWrite('servicing-editing-processed-transactions'));
UpdateCanUserEditTransactionInStatusArray(TransactionStatus.Rejected, readWrite('servicing-editing-underwritten-transactions'));
UpdateCanUserEditTransactionInStatusArray(TransactionStatus.Canceled, readWrite('servicing-editing-processed-transactions'));
UpdateCanUserEditTransactionInStatusArray(TransactionStatus.Approved, readWrite('servicing-editing-underwritten-transactions'));
UpdateCanUserEditTransactionInStatusArray(TransactionStatus.Verified, readWrite('servicing-editing-underwritten-transactions'));
UpdateCanUserEditTransactionInStatusArray(TransactionStatus.Complete, readWrite('servicing-editing-processed-transactions'));
UpdateCanUserEditTransactionInStatusArray(TransactionStatus.Declined, readWrite('servicing-editing-processed-transactions'));
UpdateCanUserEditTransactionInStatusArray(TransactionStatus.Abandoned, readWrite('servicing-editing-processed-transactions'));
UpdateCanUserEditTransactionInStatusArray(TransactionStatus.OnHold, readWrite('servicing-editing-underwritten-transactions'));

UpdateCanUserMoveTransactionToStatusArray(TransactionStatus.Pending, readWrite('servicing-moving-transactions-to-pending'));
UpdateCanUserMoveTransactionToStatusArray(TransactionStatus.Processed, readWrite('servicing-processing-transactions'));
UpdateCanUserMoveTransactionToStatusArray(TransactionStatus.Rejected, readWrite('servicing-underwriting-transactions'));
UpdateCanUserMoveTransactionToStatusArray(TransactionStatus.Canceled, readWrite('servicing-canceling-transactions'));
UpdateCanUserMoveTransactionToStatusArray(TransactionStatus.Approved, readWrite('servicing-underwriting-transactions'));
UpdateCanUserMoveTransactionToStatusArray(TransactionStatus.Verified, readWrite('servicing-processing-transactions'));
UpdateCanUserMoveTransactionToStatusArray(TransactionStatus.Complete, readWrite('servicing-processing-transactions'));
UpdateCanUserMoveTransactionToStatusArray(TransactionStatus.Declined, readWrite('servicing-processing-transactions'));
UpdateCanUserMoveTransactionToStatusArray(TransactionStatus.Abandoned, readWrite('servicing-processing-transactions'));
UpdateCanUserMoveTransactionToStatusArray(TransactionStatus.OnHold, readWrite('servicing-underwriting-transactions'));

export class TransactionQueryParams extends QueryParams {
    loanId?: number;
    renewalFor?: string;
    loanNumber?: string;
    loanFacilityId?: number;
    uniqueId?: string;
    businessName?: string;
    type?: TransactionType[];
    id?: number;
    status?: TransactionStatus[];
    nextStatus?: TransactionNextStatus;
    loanCreatedFrom?: any;
    loanCreatedTo?: any;
    companyId?: number;

    amountFrom?: number;
    amountTo?: number;

    creditLimitFrom?: number;
    creditLimitTo?: number;

    promotionIds?: number[];
    failureReasons?: string[];

    fromDate?: any;
    toDate?: any;
    transactionDate?: string;

    processDateFrom?: any;
    processDateTo?: any;
    processDate?: string;

    principalAmount?: string;
    principalAmountFrom?: number;
    principalAmountTo?: number;
    principalAmountExcept?: number;

    interestAmount?: string;
    interestAmountFrom?: number;
    interestAmountTo?: number;
    interestAmountExcept?: number;

    deferredFeeAmount?: string;
    deferredFeeAmountFrom?: number;
    deferredFeeAmountTo?: number;
    deferredFeeAmountExcept?: number;

    cashFeeAmount?: string;
    cashFeeAmountFrom?: number;
    cashFeeAmountTo?: number;
    cashFeeAmountExcept?: number;

    disbursedAmount?: string;
    disbursedAmountFrom?: number;
    disbursedAmountTo?: number;
    disbursedAmountExcept?: number;

    attribution?: TransactionAttribution;

    constructor(query?: any) {
      super(query)
      query = query || {};
      this.loanId = parseNumber(query.loanId) || undefined;
      this.renewalFor = query.renewalFor || undefined;
      this.loanNumber = query.loanNumber || undefined;
      this.loanFacilityId = parseNumber(query.loanFacilityId) || undefined;
      this.uniqueId = query.uniqueId || undefined;
      this.businessName = query.businessName || undefined;
      this.type = query.type || undefined;
      this.id = parseNumber(query.id) || undefined;
      this.status = query.status || undefined;
      this.nextStatus = parseNumber(query.nextStatus) || undefined;
      this.loanCreatedFrom = query.loanCreatedFrom || undefined;
      this.loanCreatedTo = query.loanCreatedTo || undefined;
      this.companyId = parseNumber(query.companyId) || undefined;

      this.amountFrom = query.amountFrom || undefined;
      this.amountTo = query.amountTo || undefined;

      this.creditLimitFrom = query.creditLimitFrom || undefined;
      this.creditLimitTo = query.creditLimitTo || undefined;

      this.promotionIds = parseNumberArray(query.promotionIds) || undefined;
      this.failureReasons = query.failureReasons || undefined;

      this.fromDate = query.fromDate || undefined;
      this.toDate = query.toDate || undefined;
      this.transactionDate = query.transactionDate || undefined;

      this.processDateFrom = query.processDateFrom || undefined;
      this.processDateTo = query.processDateTo || undefined;
      this.processDate = query.processDate || undefined;

      this.principalAmountFrom = parseNumber(query.principalAmountFrom);
      this.principalAmountTo = parseNumber(query.principalAmountTo);
      this.principalAmountExcept = parseNumber(query.principalAmountExcept);
      this.principalAmount = query.principalAmount || undefined;

      this.interestAmountFrom = parseNumber(query.interestAmountFrom);
      this.interestAmountTo = parseNumber(query.interestAmountTo);
      this.interestAmountExcept = parseNumber(query.interestAmountExcept);
      this.interestAmount = query.interestAmount || undefined;

      this.deferredFeeAmountFrom = parseNumber(query.deferredFeeAmountFrom);
      this.deferredFeeAmountTo = parseNumber(query.deferredFeeAmountTo);
      this.deferredFeeAmountExcept = parseNumber(query.deferredFeeAmountExcept);
      this.deferredFeeAmount = query.deferredFeeAmount || undefined;

      this.cashFeeAmountFrom = parseNumber(query.cashFeeAmountFrom);
      this.cashFeeAmountTo = parseNumber(query.cashFeeAmountTo);
      this.cashFeeAmountExcept = parseNumber(query.cashFeeAmountExcept);
      this.cashFeeAmount = query.cashFeeAmount || undefined;

      this.disbursedAmountFrom = parseNumber(query.disbursedAmountFrom);
      this.disbursedAmountTo = parseNumber(query.disbursedAmountTo);
      this.disbursedAmountExcept = parseNumber(query.disbursedAmountExcept);
      this.disbursedAmount = query.disbursedAmount || undefined;

      this.attribution = query.attribution || undefined;
    }
}

export interface TransactionQueryResult extends QueryResult<TransactionData> {
}

export interface TransactionActionCommand {
    id?: number;
    status?: number;
    nextStatus?: number;
    type?: TransactionType;
    transferMethod?: TransferMethod,
    transferSource?: TransferSource,
    notes?: string[];
    failureReasons?: string[];
    loanId?: number;
    date?: Date;
    processDate?: Date;
    underwritingDate?: Date;
    financeDate?: Date;
    completeDate?: Date;
    cancellationDate?: Date;
    declinedDate?: Date;
    amount?: number;
    subtransactions?: SubtransactionData[];
    initiatorType?: InitiatorType,
    initiatorId?: number
    promotions?: number[];
    competitors?: number[];
    drawRequirementsHash?: string;
}


export const TransactionStatusChips = {
    '0': { color: 'mat-grey-bg', icon: 'help', label: 'Pending' },
    '2': { color: 'mat-red-bg', icon: 'block', label: 'Rejected' },
    '3': { color: 'mat-orange-bg', icon: 'highlight_off', label: 'Canceled' },
    '4': { color: 'mat-green-bg', icon: 'check_circle', label: 'Approved' },
    '5': { color: 'mat-indigo-bg', icon: 'done_all', label: 'Verified' },
    '7': { color: 'mat-purple-bg', icon: 'warning', label: 'Declined' },
    '8': { color: 'mat-red-bg', icon: 'sentiment_very_dissatisfied', label: 'Abandoned' },
    '9': { color: 'mat-indigo-bg', icon: 'help', label: 'On Hold' }
};

export class TransactionFilters {
    loanId: number;
    loanNumber: any;
    loanFacilityId: any;
    businessName: any;
    type: TransactionType[];
    id: number;
    status: TransactionStatus[];
    nextStatus: TransactionNextStatus;
    companyId: number;

    amountFrom: number;
    amountTo: number;

    creditLimitFrom: number;
    creditLimitTo: number;

    promotionIds: number[];
    failureReasons: string[];

    fromDate: Date;
    toDate: Date;
    transactionDate: string;

    processDateFrom: Date;
    processDateTo: Date;
    processDate: string;

    principalAmountFrom?: number;
    principalAmountTo?: number;
    principalAmountExcept?: number;
    principalAmount?: string;

    interestAmountFrom?: number;
    interestAmountTo?: number;
    interestAmountExcept?: number;
    interestAmount?: string;

    deferredFeeAmountFrom?: number;
    deferredFeeAmountTo?: number;
    deferredFeeAmountExcept?: number;
    deferredFeeAmount?: string;

    cashFeeAmountFrom?: number;
    cashFeeAmountTo?: number;
    cashFeeAmountExcept?: number;
    cashFeeAmount?: string;

    disbursedAmountFrom?: number;
    disbursedAmountTo?: number;
    disbursedAmountExcept?: number;
    disbursedAmount?: string;

    constructor(query?: TransactionQueryParams) {
        query = query || {};
        this.loanId = query.loanId || undefined;
        this.loanNumber = query.loanNumber || undefined;
        this.loanFacilityId = query.loanFacilityId || undefined;
        this.businessName = query.businessName || undefined;
        this.id = query.id || undefined;
        this.companyId = query.companyId;
        this.type = query.type;
        this.status = query.status;
        this.nextStatus = query.nextStatus;

        this.amountFrom = query.amountFrom || undefined;
        this.amountTo = query.amountTo || undefined;

        this.creditLimitFrom = query.creditLimitFrom || undefined;
        this.creditLimitTo = query.creditLimitTo || undefined;

        this.promotionIds = query.promotionIds;
        this.failureReasons = query.failureReasons;

        this.fromDate = query.fromDate || undefined;
        this.toDate = query.toDate || undefined;
        this.transactionDate = query.transactionDate || undefined;

        this.processDateFrom = query.processDateFrom || undefined;
        this.processDateTo = query.processDateTo || undefined;
        this.processDate = query.processDate || undefined;

        this.principalAmountFrom = query.principalAmountFrom;
        this.principalAmountTo = query.principalAmountTo;
        this.principalAmountExcept = query.principalAmountExcept;
        this.principalAmount = query.principalAmount || undefined;

        this.interestAmountFrom = query.interestAmountFrom;
        this.interestAmountTo = query.interestAmountTo;
        this.interestAmountExcept = query.interestAmountExcept;
        this.interestAmount = query.interestAmount || undefined;

        this.deferredFeeAmountFrom = query.deferredFeeAmountFrom;
        this.deferredFeeAmountTo = query.deferredFeeAmountTo;
        this.deferredFeeAmountExcept = query.deferredFeeAmountExcept;
        this.deferredFeeAmount = query.deferredFeeAmount || undefined;

        this.cashFeeAmountFrom = query.cashFeeAmountFrom;
        this.cashFeeAmountTo = query.cashFeeAmountTo;
        this.cashFeeAmountExcept = query.cashFeeAmountExcept;
        this.cashFeeAmount = query.cashFeeAmount || undefined;

        this.disbursedAmountFrom = query.disbursedAmountFrom;
        this.disbursedAmountTo = query.disbursedAmountTo;
        this.disbursedAmountExcept = query.disbursedAmountExcept;
        this.disbursedAmount = query.disbursedAmount || undefined;
    }

    remove(item: string) {
        this[item] = undefined;
        if (item === 'fromDate') {
            this.toDate = undefined;
            this.transactionDate = undefined;
        }
        if (item === 'processDateFrom') {
            this.processDateTo = undefined;
            this.processDate = undefined;
        }

        if (item === 'principalAmountFrom' || item === 'principalAmountTo' || item === 'principalAmountExcept') {
            this.principalAmount = undefined;
        }
        if (item === 'interestAmountFrom' || item === 'interestAmountTo' || item === 'interestAmountExcept') {
            this.interestAmount = undefined;
        }
        if (item === 'deferredFeeAmountFrom' || item === 'deferredFeeAmountTo' || item === 'deferredFeeAmountExcept') {
            this.deferredFeeAmount = undefined;
        }
        if (item === 'cashFeeAmountFrom' || item === 'cashFeeAmountTo' || item === 'cashFeeAmountExcept') {
            this.cashFeeAmount = undefined;
        }
        if (item === 'disbursedAmountFrom' || item === 'disbursedAmountTo' || item === 'disbursedAmountExcept') {
            this.disbursedAmount = undefined;
        }
    }
}

export interface TransactionListComponentData {
    transactionQueryResult: TransactionQueryResult;
    account: AccountData;
    accountInfo: LoanInfoDto;
    failureReasons: FailureReasonBaseData[];
    accountAmortizationInfo: AccountAmortizationData[];
    userCurrent?: UserData;
    promotionsToSelect?: PromotionSelectOptionData[];
    liquidityEvent: LiquidityEvent;
    accountTransactionsList: boolean;
}

export interface TransactionsGroupedByAdvisor {
    advisor?: AdvisorData;
    totalAmount?: number;
    totalCount?: number;
}

export interface DrawRequirementData {
    type: DrawRequirementType,
    requirementCompleted: boolean,
    requirementCompletedOnUtc: Date,
    required: boolean,
    advisory: boolean,
    customerId?: number,
    name?: string,
}

export interface SimulationTransactionData {
  accountTransferData: AccountTransferData;
  accountId: number;
  amountInput: string;
  amountMinimum: number;
  amountMaximum: number;
  suggestedDrawAmount?: number;
  weeklyPaymentAmount?: number;
  paymentFrequency?: PaymentFrequency;
  productCode?: ProductCode;
}

export enum DrawRequirementType {
    TwoUniquePhoneNumbers = "two-unique-phone-numbers",
    UCCFilingCompleted = "ucc-filing-completed",
    WelcomeCallCompleted = "welcome-call-completed",
    RequestorLocationMatchesBusinessAddress = "requestor-location-matches-business-address",
    IdVerification = "id-verification",
    RenewalApplicationRequiredStip = "renewal-application-required-stip",
    MissingRequiredBankIntegration = "missing-required-bank-integration",
    PendingDrawsOnEntity = "pending-draws-on-entity",
    CompletedDrawsInLast60Days = "completed-draws-in-last-60-days",
    PostDrawUtilizationElevated = "post-draw-utilization-elevated",
    PostDrawUtilizationHigh = "post-draw-utilization-high",
    PostDrawBalanceOverThreshold = "post-draw-balance-over-threshold",
    RecentPaymentsOnEntity = "recent-payments-on-entity"
}

export enum TransactionAttribution {
    Activation = "activation",
    Residual = "residual",
}

export interface DrawRequirementLabel {
  text: string;
  html?: string;
}

export interface DrawRequirementLabelContext {
  requestorState?: string;
  businessState?: string;
}

export const DrawRequirementTypeLabel = new Map<
  string,
  (r: DrawRequirementData, ctx: DrawRequirementLabelContext) => DrawRequirementLabel
>([
  [DrawRequirementType.WelcomeCallCompleted, (r): DrawRequirementLabel => ({
    text: r.requirementCompleted
      ? `Welcome call completed on ${new Date(r.requirementCompletedOnUtc).toLocaleDateString()}`
      : "Welcome call not completed yet"
  })],

  [DrawRequirementType.TwoUniquePhoneNumbers, (r): DrawRequirementLabel => ({
    text: r.requirementCompleted ? "Unique phone numbers met" : "Unique phone numbers not met"
  })],

  [DrawRequirementType.UCCFilingCompleted, (r): DrawRequirementLabel => ({
    text: r.requirementCompleted
      ? `UCC Filing met on ${new Date(r.requirementCompletedOnUtc).toLocaleDateString()}`
      : "UCC Filing not met"
  })],

  [DrawRequirementType.RequestorLocationMatchesBusinessAddress, (r, ctx): DrawRequirementLabel => {
    const requestor = ctx.requestorState ? `(${ctx.requestorState}) ` : "";
    return {
      text: r.requirementCompleted
        ? `Requestor's location ${requestor}matches business address (${ctx.businessState})`
        : `Requestor's location ${requestor}differs from business address (${ctx.businessState})`
    };
  }],

  [DrawRequirementType.IdVerification, (r): DrawRequirementLabel => {
    const date = new Date(r.requirementCompletedOnUtc).toLocaleDateString();
    if (r.requirementCompleted) {
      return { text: `ID verification passed on ${date}` };
    }
    return {
      text: `ID verification passed with warnings on ${date}`,
      html: `ID <a target="_blank" rel="noopener noreferrer" href="/customer/${r.customerId}/profile">verification</a> passed with warnings on ${date}`
    };
  }],

  [DrawRequirementType.RenewalApplicationRequiredStip, (r): DrawRequirementLabel => ({
    text: `Renewal stip: ${r.name}`
  })],

  [DrawRequirementType.MissingRequiredBankIntegration, (): DrawRequirementLabel => ({
    text: "Missing required bank integration"
  })],

  [DrawRequirementType.PendingDrawsOnEntity, (r): DrawRequirementLabel => ({
    text: r.name
  })],

  [DrawRequirementType.CompletedDrawsInLast60Days, (r): DrawRequirementLabel => ({
    text: r.name
  })],

  [DrawRequirementType.PostDrawUtilizationElevated, (r): DrawRequirementLabel => ({
    text: r.name
  })],

  [DrawRequirementType.PostDrawUtilizationHigh, (r): DrawRequirementLabel => ({
    text: r.name
  })],

  [DrawRequirementType.PostDrawBalanceOverThreshold, (r): DrawRequirementLabel => ({
    text: r.name
  })],

  [DrawRequirementType.RecentPaymentsOnEntity, (r): DrawRequirementLabel => ({
    text: r.name
  })]
]);

export enum DrawRequirementSeverity {
  NeedsAttention = "needs-attention",
  Warning = "warning",
  Satisfied = "satisfied"
}

export function getDrawRequirementSeverity(requirement: DrawRequirementData): DrawRequirementSeverity {
  if (requirement.requirementCompleted) {
    return DrawRequirementSeverity.Satisfied;
  }
  return requirement.required ? DrawRequirementSeverity.NeedsAttention : DrawRequirementSeverity.Warning;
}

export interface GroupedDrawRequirementLabels {
  needsAttention: DrawRequirementLabel[];
  warnings: DrawRequirementLabel[];
  satisfied: DrawRequirementLabel[];
}

// Resolves each requirement to its display label and buckets it by severity. Returned arrays
// preserve the original requirement order within each group.
export function groupDrawRequirementLabels(
  requirements: DrawRequirementData[] | undefined,
  ctx: DrawRequirementLabelContext
): GroupedDrawRequirementLabels {
  const grouped: GroupedDrawRequirementLabels = { needsAttention: [], warnings: [], satisfied: [] };

  requirements?.forEach(requirement => {
    const label = DrawRequirementTypeLabel.get(requirement.type)?.(requirement, ctx);
    if (!label) {
      return;
    }

    switch (getDrawRequirementSeverity(requirement)) {
      case DrawRequirementSeverity.NeedsAttention:
        grouped.needsAttention.push(label);
        break;
      case DrawRequirementSeverity.Warning:
        grouped.warnings.push(label);
        break;
      default:
        grouped.satisfied.push(label);
    }
  });

  return grouped;
}

// A render-ready section for the HTML surfaces (approval dialog, details card). The title carries
// the same glyph language as the transaction-list tooltip; items are pre-resolved to html-or-text.
export interface DrawRequirementDisplayGroup {
  title: string;
  items: string[];
}

export const DRAW_REQUIREMENT_GROUP_TITLE = {
  needsAttention: "⛔ Needs attention",
  warnings: "⚠️ Warnings",
  satisfied: "✅ Satisfied"
};

export function toDrawRequirementDisplayGroups(
  grouped: GroupedDrawRequirementLabels,
  includeSatisfied: boolean
): DrawRequirementDisplayGroup[] {
  const toItems = (labels: DrawRequirementLabel[]): string[] => labels.map(label => label.html ?? label.text);

  const groups: DrawRequirementDisplayGroup[] = [];
  if (grouped.needsAttention.length) {
    groups.push({ title: DRAW_REQUIREMENT_GROUP_TITLE.needsAttention, items: toItems(grouped.needsAttention) });
  }
  if (grouped.warnings.length) {
    groups.push({ title: DRAW_REQUIREMENT_GROUP_TITLE.warnings, items: toItems(grouped.warnings) });
  }
  if (includeSatisfied && grouped.satisfied.length) {
    groups.push({ title: DRAW_REQUIREMENT_GROUP_TITLE.satisfied, items: toItems(grouped.satisfied) });
  }
  return groups;
}

export interface TransactionUpdateResult {
    previousLoan?: AccountDto;
    currentLoan?: AccountDto;
    relatedTransactionId?: number;
    declineRecalculationResultCode?: DeclineRecalculationResultCode;
}

export interface AccountDto {
    id?: number;
    uniqueId?: string;
    active?: boolean;
    status?: LoanStatus;
    loanNumber?: string;
    entityId?: number;
    advisorId?: number;
    description?: string;
    product?: ProductDto;
    freezeDate?: Date;
    frozenUntil?: Date;
    lastMaintenanceDate?: Date;
    fullAmortizationStartDateUtc?: Date;
    minReversalAllowedDateUtc?: Date;
    currentState?: AccountStateDto;
    cutoffState?: AccountStateDto;
    maintenanceState?: AccountStateDto;
}

export interface ProductDto {
    id?: number;
    code?: string;
    name?: string;
    reversibleDaysPeriod?: number;
}

export enum DeclineRecalculationResultCode {
    Success = "success",
    TransactionProcessedOverXDaysAgo = "transaction-processed-over-x-days-ago",
    TransactionWasNotMaintained = "transaction-was-not-maintained",
    TransactionNotDeclined = "transaction-not-declined",
    TransactionTypeNotSupported = "transaction-type-not-supported",
    LoanTypeNotSupported = "loan-type-not-supported"
}
